import { useEffect, useRef, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { useRouter } from "@tanstack/react-router";

/**
 * The phone menu: a modal sheet that closes from its grabber, the backdrop, Escape (native to a
 * modal dialog), a chosen link, a downward swipe on phones, or the next navigation.
 */
export function MenuSheet({ children }: { children: ReactNode }) {
  const toggle = useRef<HTMLButtonElement>(null);
  const sheet = useRef<HTMLDialogElement>(null);
  const drag = useRef<{
    pointer: number;
    startY: number;
    lastY: number;
    lastTime: number;
    velocity: number;
    moved: boolean;
  } | null>(null);
  const router = useRouter();
  const close = () => {
    if (sheet.current?.open) sheet.current.close();
  };
  useEffect(() => {
    const unsubscribe = router.subscribe("onBeforeNavigate", close);
    // A widened window hides the toggle; its sheet would otherwise leave the page inert.
    const onResize = () => {
      if (!toggle.current?.getClientRects().length) close();
    };
    addEventListener("resize", onResize);
    return () => {
      unsubscribe();
      removeEventListener("resize", onResize);
    };
  }, [router]);
  const open = () => {
    const dialog = sheet.current;
    if (!dialog) return;
    const list = dialog.querySelector("nav");
    list?.scrollTo(0, 0);
    dialog.showModal();
    dialog.focus();
    toggle.current?.setAttribute("aria-expanded", "true");
    // Short menus drag from anywhere; one long enough to scroll drags from its grabber.
    if (list) list.style.touchAction = list.scrollHeight > list.clientHeight ? "" : "none";
  };
  const onPointerDown = (event: ReactPointerEvent<HTMLDialogElement>) => {
    const dialog = sheet.current;
    if (!dialog?.open || !event.isPrimary || event.button !== 0) return;
    if (!matchMedia("(max-width: 600px)").matches) return;
    if ((dialog.querySelector("nav")?.scrollTop ?? 0) > 0) return;
    drag.current = {
      pointer: event.pointerId,
      startY: event.clientY,
      lastY: event.clientY,
      lastTime: event.timeStamp,
      velocity: 0,
      moved: false,
    };
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLDialogElement>) => {
    const state = drag.current;
    const dialog = sheet.current;
    if (!state || !dialog || event.pointerId !== state.pointer) return;
    const offset = event.clientY - state.startY;
    if (!state.moved) {
      // A small movement is still a tap; an upward one is not a dismissal.
      if (offset < -8) drag.current = null;
      if (!drag.current || offset < 8) return;
      state.moved = true;
      dialog.setPointerCapture(event.pointerId);
      dialog.dataset.dragging = "";
    }
    state.velocity = (event.clientY - state.lastY) / Math.max(1, event.timeStamp - state.lastTime);
    state.lastY = event.clientY;
    state.lastTime = event.timeStamp;
    dialog.style.translate = `0 ${Math.max(0, offset)}px`;
  };
  const endDrag = (event: ReactPointerEvent<HTMLDialogElement>) => {
    const state = drag.current;
    const dialog = sheet.current;
    if (!state || !dialog || event.pointerId !== state.pointer) return;
    drag.current = null;
    if (!state.moved) return;
    delete dialog.dataset.dragging;
    const offset = event.clientY - state.startY;
    if (event.type === "pointerup" && (offset > dialog.offsetHeight / 3 || state.velocity > 0.5))
      dialog.close();
    dialog.style.translate = "";
  };
  return (
    <div className="mobile-menu">
      <button
        ref={toggle}
        className="menu-toggle"
        type="button"
        aria-label="Menu"
        aria-haspopup="dialog"
        aria-controls="menu-sheet"
        aria-expanded="false"
        onClick={open}
      >
        <span className="menu-icon" aria-hidden="true" />
      </button>
      <dialog
        ref={sheet}
        className="menu-sheet"
        id="menu-sheet"
        aria-label="Menu"
        tabIndex={-1}
        onClose={() => toggle.current?.setAttribute("aria-expanded", "false")}
        onClick={(event) => {
          const dialog = sheet.current;
          const target = event.target instanceof Element ? event.target : null;
          if (!dialog || !target) return;
          const box = dialog.getBoundingClientRect();
          const outside =
            event.clientX < box.left ||
            event.clientX > box.right ||
            event.clientY < box.top ||
            event.clientY > box.bottom;
          if ((target === dialog && outside) || target.closest(".menu-sheet-close, a")) close();
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <button className="menu-sheet-close" type="button" aria-label="Close menu" />
        <nav aria-label="Menu navigation">{children}</nav>
      </dialog>
    </div>
  );
}
